import React from 'react';

import type { ICheckboxProps } from './Checkbox.types';
import { Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

export const Checkbox: React.FC<ICheckboxProps> = ({
  label,
  helperText,
  errorText,
  size = 'md',
  variant = 'default',
  className = '',
  containerClassName = '',
  labelClassName = '',
  required = false,
  disabled = false,
  id,
  ...checkboxProps
}: ICheckboxProps) => {

  // Size mapping for checkbox
  const sizeMapper = {
    'sm': {
      checkbox: 'w-4 h-4',
      text: 'text-bg-v2-sm',
      gap: 'gap-bg-v2-xs',
      checkmark: 'w-2.5 h-2.5',
    },
    'md': {
      checkbox: 'w-5 h-5',
      text: 'text-bg-v2-sm',
      gap: 'gap-bg-v2-sm',
      checkmark: 'w-3 h-3',
    },
    'lg': {
      checkbox: 'w-6 h-6',
      text: 'text-bg-v2-lg',
      gap: 'gap-bg-v2-md',
      checkmark: 'w-4 h-4',
    },
  };

  // Variant mapping for border and focus states
  const variantMapper = {
    'default': 'border-border-primary font-bg-v2-sans font-bg-v2-regular bg-bg-primary checked:bg-bg-brand-solid checked:border-border-brand focus:border-border-brand focus:border-2',
    'error': 'border-border-error font-bg-v2-sans font-bg-v2-regular bg-bg-primary checked:bg-bg-error-solid checked:border-border-error focus:border-border-error focus:border-2',
    'success': 'border-border-brand font-bg-v2-sans font-bg-v2-regular bg-bg-primary checked:bg-bg-brand-solid checked:border-border-brand focus:border-border-brand focus:border-2',
  };

  // Determine actual variant based on errorText
  const actualVariant = errorText ? 'error' : variant;

  // Generate unique ID if not provided
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

  // Build checkbox classes
  const checkboxClasses = `
    ${sizeMapper[size].checkbox}
    ${variantMapper[actualVariant]}
    border
    rounded-bg-v2-sm
    transition-all
    transition-colors
    duration-200
    focus:outline-none
    appearance-none
    cursor-pointer
    flex
    items-center
    justify-center
    disabled:bg-bg-disabled
    disabled:border-border-disabled
    disabled:cursor-not-allowed
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={`flex flex-col gap-bg-v2-xl w-full ${containerClassName}`}>
      <div className={`flex items-start ${sizeMapper[size].gap}`}>
        {/* Checkbox with Checkmark */}
        <div className="relative inline-flex items-center justify-center">
          <input
            type="checkbox"
            id={checkboxId}
            disabled={disabled}
            required={required}
            className={`${checkboxClasses} peer`}
            {...checkboxProps}
          />
          {/* Checkmark Icon - Positioned absolutely inside checkbox */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity duration-200">
            <Icon 
              name={IconName.Check} 
              size="sm" 
              className='text-text-white' 
              inheritColor 
            />
          </div>
        </div>

        {/* Label */}
        {label && (
          <div className="flex flex-col flex-1">
            <label
              htmlFor={checkboxId}
              className={`
                font-bg-v2-sans
                font-bg-v2-regular
                ${sizeMapper[size].text}
                ${errorText ? 'text-text-error-primary' : 'text-text-secondary'}
                ${disabled ? 'text-text-disabled cursor-not-allowed' : 'cursor-pointer'}
                ${labelClassName}
              `.trim().replace(/\s+/g, ' ')}
            >
              {label}
            </label>
          </div>
        )}
      </div>

      {/* Helper Text or Error Text */}
      {(helperText || errorText) && (
        <span
          className={`text-bg-v2-sm ${errorText
            ? 'text-text-error-primary'
            : 'text-text-tertiary'
            }`}
        >
          {errorText || helperText}
        </span>
      )}
    </div>
  );
};

